import { zodResolver } from '@hookform/resolvers/zod';
import type { TFunction } from 'i18next';
import { CheckIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { InputDate } from '@/components/ui/input-date';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useFormatDate } from '@/hooks/use-format-date';
import type { TranslationKey } from '@/i18n/types';
import { cn } from '@/lib/utils';
import { addDays, startOfWeek } from 'date-fns';
import { useMemo } from 'react';
import { SchedulePastille } from '../schedule-pastille';

export enum SchedulingMode {
  daily = 'daily',
  every_2_days = 'every_2_days',
  every_3_days = 'every_3_days',
  every_4_days = 'every_4_days',
  every_5_days = 'every_5_days',
  every_6_days = 'every_6_days',
  weekly = 'weekly',
  every_2_weeks = 'every_2_weeks',
  monthly = 'monthly',
}

const rotationModeLabels: Record<SchedulingMode, TranslationKey> = {
  [SchedulingMode.daily]: 'enums.scheduleMode.daily',
  [SchedulingMode.every_2_days]: 'enums.scheduleMode.every_2_days',
  [SchedulingMode.every_3_days]: 'enums.scheduleMode.every_3_days',
  [SchedulingMode.every_4_days]: 'enums.scheduleMode.every_4_days',
  [SchedulingMode.every_5_days]: 'enums.scheduleMode.every_5_days',
  [SchedulingMode.every_6_days]: 'enums.scheduleMode.every_6_days',
  [SchedulingMode.weekly]: 'enums.scheduleMode.weekly',
  [SchedulingMode.every_2_weeks]: 'enums.scheduleMode.every_2_weeks',
  [SchedulingMode.monthly]: 'enums.scheduleMode.monthly',
};

const predefinedColors = [
  { value: '#2b7fff', label: 'Blue' },
  { value: '#6554c0', label: 'Purple' },
  { value: '#ff5630', label: 'Red' },
  { value: '#36b37e', label: 'Green' },
  { value: '#ff8b00', label: 'Orange' },
  { value: '#00c2a3', label: 'Teal' },
  { value: '#ffcc00', label: 'Yellow' },
  { value: '#bf2600', label: 'Dark Red' },
  { value: '#6b778c', label: 'Slate' },
  { value: '#403294', label: 'Indigo' },
];

export const getFormSchema = (t: TFunction) =>
  z.object({
    name: z
      .string({
        required_error: t(
          'features.scheduleForm.form.fields.name.error.required'
        ),
      })
      .min(1, {
        message: t('features.scheduleForm.form.fields.name.error.required'),
      }),
    description: z.string().optional(),
    startDate: z.date({
      required_error: t(
        'features.scheduleForm.form.fields.startDate.error.required'
      ),
    }),
    endDate: z.date().optional().nullable(),
    daysOfWeek: z.array(z.number()).min(1, {
      message: t('features.scheduleForm.form.fields.daysOfWeek.error.required'),
    }),
    mode: z.nativeEnum(SchedulingMode, {
      required_error: t(
        'features.scheduleForm.form.fields.mode.error.required'
      ),
    }),
    timezone: z.string({
      required_error: t(
        'features.scheduleForm.form.fields.timezone.error.required'
      ),
    }),
    color: z.string().default(predefinedColors[0].value),
    minOnDuty: z
      .number()
      .int()
      .min(1, t('features.scheduleForm.form.fields.minOnDuty.error.min'))
      .default(1),
  });

const timezones = [
  'Europe/Paris',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Madrid',
  'America/New_York',
  'America/Los_Angeles',
  'America/Chicago',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
  'Pacific/Auckland',
];

interface ScheduleFormProps {
  defaultValues?: Partial<z.infer<ReturnType<typeof getFormSchema>>>;
  submitLabel: string;
  isSubmitting: boolean;
  onSubmit: (values: z.infer<ReturnType<typeof getFormSchema>>) => void;
}

export function ScheduleForm({
  defaultValues,
  submitLabel,
  isSubmitting,
  onSubmit,
}: ScheduleFormProps) {
  const { t } = useTranslation();
  const { formatWithPreset } = useFormatDate();

  const formSchema = getFormSchema(t);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      daysOfWeek: [],
      mode: SchedulingMode.daily,
      timezone: 'Europe/Paris',
      color: predefinedColors[0].value,
      minOnDuty: undefined,
      ...defaultValues,
    },
  });

  const daysOfWeek = useMemo(() => {
    const first = startOfWeek(new Date(), { weekStartsOn: 1 });

    return Array.from({ length: 7 }, (_, index) => {
      const date = addDays(first, index);
      return {
        value: index + 1,
        label: formatWithPreset(date, 'long_weekday'),
      };
    });
  }, [formatWithPreset]);

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    onSubmit(values);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        <div>
          <h3 className="text-lg font-medium mb-4">
            {t('features.scheduleForm.form.scheduleInformation')}
          </h3>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('features.scheduleForm.form.fields.name.label')}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t(
                      'features.scheduleForm.form.fields.name.placeholder'
                    )}
                    autoComplete="off"
                    data-1p-ignore
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="mt-4">
                <FormLabel>
                  {t('features.scheduleForm.form.fields.description.label')}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t(
                      'features.scheduleForm.form.fields.description.placeholder'
                    )}
                    autoComplete="off"
                    data-1p-ignore
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="color"
            render={({ field }) => (
              <FormItem className="mt-4">
                <FormLabel>
                  {t('features.scheduleForm.form.fields.color.label')}
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex flex-wrap gap-3"
                  >
                    {predefinedColors.map((color) => (
                      <div key={color.value} className="flex items-center">
                        <RadioGroupItem
                          value={color.value}
                          id={`color-${color.value.replace('#', '')}`}
                          className="peer sr-only"
                        />
                        <label
                          htmlFor={`color-${color.value.replace('#', '')}`}
                          className={cn(
                            'h-6 w-6 rounded-full cursor-pointer flex items-center justify-center transition-all overflow-hidden',
                            field.value === color.value
                              ? 'ring-2 ring-primary ring-opacity-20'
                              : ''
                          )}
                          title={color.label}
                        >
                          <SchedulePastille
                            color={color.value}
                            size="lg"
                            className="w-full h-full"
                          />
                          {field.value === color.value && (
                            <CheckIcon className="h-4 w-4 text-white drop-shadow-sm absolute" />
                          )}
                        </label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator />

        <div>
          <h3 className="text-lg font-medium mb-4">
            {t('features.scheduleForm.form.dateAndTime')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormField
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <InputDate
                  field={field}
                  label={t('features.scheduleForm.form.fields.startDate.label')}
                  placeholder={t(
                    'features.scheduleForm.form.fields.startDate.placeholder'
                  )}
                />
              )}
            />

            <FormField
              control={form.control}
              name="endDate"
              render={({ field }) => (
                <InputDate
                  field={field}
                  label={t('features.scheduleForm.form.fields.endDate.label')}
                  placeholder={t(
                    'features.scheduleForm.form.fields.endDate.placeholder'
                  )}
                  disabled={(date) =>
                    date < (form.watch('startDate') || new Date())
                  }
                  clearable
                />
              )}
            />

            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>
                    {t('features.scheduleForm.form.fields.timezone.label')}
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full h-10">
                        <SelectValue
                          placeholder={t(
                            'features.scheduleForm.form.fields.timezone.placeholder'
                          )}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {timezones.map((timezone) => (
                        <SelectItem key={timezone} value={timezone}>
                          {timezone}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <Separator />

        <div>
          <h3 className="text-lg font-medium mb-4">
            {t('features.scheduleForm.form.scheduleConfiguration')}
          </h3>
          <FormField
            control={form.control}
            name="minOnDuty"
            render={({ field }) => (
              <FormItem className="mb-6">
                <FormLabel>
                  {t('features.scheduleForm.form.fields.minOnDuty.label')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    {...field}
                    value={field.value}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>
                  {t('features.scheduleForm.form.fields.minOnDuty.description')}
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="daysOfWeek"
            render={({ field }) => (
              <FormItem className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <FormLabel>
                    {t('features.scheduleForm.form.fields.daysOfWeek.label')}
                  </FormLabel>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => {
                      const allDayValues = daysOfWeek.map((day) => day.value);
                      const hasAllDays = allDayValues.every((value) =>
                        field.value.includes(value)
                      );

                      field.onChange(hasAllDays ? [] : allDayValues);
                    }}
                  >
                    {daysOfWeek.length === field.value.length
                      ? t(
                          'features.scheduleForm.form.fields.daysOfWeek.deselectAll'
                        )
                      : t(
                          'features.scheduleForm.form.fields.daysOfWeek.selectAll'
                        )}
                  </Button>
                </div>

                <ButtonGroup
                  values={daysOfWeek}
                  value={field.value}
                  onChange={field.onChange}
                  multiple={true}
                  layout="flex"
                />
                <FormMessage />
                <FormDescription>
                  {t(
                    'features.scheduleForm.form.fields.daysOfWeek.description'
                  )}
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="mode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('features.scheduleForm.form.fields.mode.label')}
                </FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={t(
                          'features.scheduleForm.form.fields.mode.placeholder'
                        )}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={SchedulingMode.daily}>
                      {t(rotationModeLabels[SchedulingMode.daily])}
                    </SelectItem>

                    {[
                      SchedulingMode.every_2_days,
                      SchedulingMode.every_3_days,
                      SchedulingMode.every_4_days,
                      SchedulingMode.every_5_days,
                      SchedulingMode.every_6_days,
                    ].map((mode) => (
                      <SelectItem key={mode} value={mode}>
                        {t(rotationModeLabels[mode])}
                      </SelectItem>
                    ))}

                    <SelectItem value={SchedulingMode.weekly}>
                      {t(rotationModeLabels[SchedulingMode.weekly])}
                    </SelectItem>

                    <SelectItem value={SchedulingMode.every_2_weeks}>
                      {t(rotationModeLabels[SchedulingMode.every_2_weeks])}
                    </SelectItem>
                    <SelectItem value={SchedulingMode.monthly}>
                      {t(rotationModeLabels[SchedulingMode.monthly])}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
                <div className="h-6 mt-2">
                  {[
                    SchedulingMode.weekly,
                    SchedulingMode.every_2_weeks,
                  ].includes(field.value) && (
                    <FormDescription>
                      {t('features.scheduleForm.form.fields.mode.description')}
                    </FormDescription>
                  )}
                </div>
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end space-x-4">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? t('features.scheduleForm.form.submitting')
              : submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
